import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * WidgetPostService
 * Proxy de todas las llamadas que PostComponent hace via UserService/FeeService,
 * pero autenticadas con widget JWT (X-Skip-Auth-Interceptor + token query param).
 */
@Injectable({
  providedIn: 'root'
})
export class WidgetPostService {
  private baseUrl = environment.BASE_LA;

  constructor(private http: HttpClient) {}

  private getHeaders(): { [key: string]: string } {
    return { 'X-Skip-Auth-Interceptor': 'true', 'content-type': 'application/json' };
  }

  private getParams(token: string, extra: any = {}): HttpParams {
    let params = new HttpParams().set('token', token);
    Object.keys(extra).forEach(k => {
      if (extra[k] !== undefined && extra[k] !== null) {
        params = params.set(k, extra[k]);
      }
    });
    return params;
  }

  // ========================================================================
  // GET /api/widget/post/:personId/person
  // ========================================================================
  getPersonbyCode(personId: string, token: string): Promise<any> {
    return lastValueFrom(
      this.http.get<any>(
        `${this.baseUrl}/api/widget/post/${personId}/person`,
        { headers: this.getHeaders(), params: this.getParams(token) }
      )
    );
  }

  // ========================================================================
  // GET /api/widget/post/:personId/accounts
  // ========================================================================
  account_payment(personId: string, token: string, params: any = {}): Promise<any> {
    return lastValueFrom(
      this.http.get<any>(
        `${this.baseUrl}/api/widget/post/${personId}/accounts`,
        { headers: this.getHeaders(), params: this.getParams(token, params) }
      )
    );
  }

  // ========================================================================
  // POST /api/widget/post/:personId/rate
  // ========================================================================
  getRate(personId: string, token: string, body: any = {}): Promise<any> {
    return lastValueFrom(
      this.http.post<any>(
        `${this.baseUrl}/api/widget/post/${personId}/rate`,
        body,
        { headers: this.getHeaders(), params: this.getParams(token) }
      )
    );
  }

  // ========================================================================
  // POST /api/widget/post/:personId/deductions
  // ========================================================================
  getDeduction_payment(personId: string, token: string): Promise<any> {
    return lastValueFrom(
      this.http.post<any>(
        `${this.baseUrl}/api/widget/post/${personId}/deductions`,
        {},
        { headers: this.getHeaders(), params: this.getParams(token) }
      )
    );
  }

  // ========================================================================
  // GET /api/widget/post/:personId/banks
  // ========================================================================
  getBanks(personId: string, token: string): Promise<any> {
    return lastValueFrom(
      this.http.get<any>(
        `${this.baseUrl}/api/widget/post/${personId}/banks`,
        { headers: this.getHeaders(), params: this.getParams(token) }
      )
    );
  }

  // ========================================================================
  // GET /api/widget/post/:personId/type-products
  // ========================================================================
  getTypeProducts(personId: string, token: string): Promise<any> {
    return lastValueFrom(
      this.http.get<any>(
        `${this.baseUrl}/api/widget/post/${personId}/type-products`,
        { headers: this.getHeaders(), params: this.getParams(token) }
      )
    );
  }

  // ========================================================================
  // GET /api/widget/post/:personId/products-invoices
  // ========================================================================
  getTypesProductsInvoices(personId: string, token: string): Promise<any> {
    return lastValueFrom(
      this.http.get<any>(
        `${this.baseUrl}/api/widget/post/${personId}/products-invoices`,
        { headers: this.getHeaders(), params: this.getParams(token) }
      )
    );
  }

  // ========================================================================
  // GET /api/widget/post/:personId/codes
  // ========================================================================
  getAllCodes(personId: string, token: string): Promise<any> {
    return lastValueFrom(
      this.http.get<any>(
        `${this.baseUrl}/api/widget/post/${personId}/codes`,
        { headers: this.getHeaders(), params: this.getParams(token) }
      )
    );
  }

  // ========================================================================
  // POST /api/widget/post/:personId/installments
  // ========================================================================
  getPersonInstallmentsInvoiceCredits(personId: string, token: string, body: any): Promise<any> {
    return lastValueFrom(
      this.http.post<any>(
        `${this.baseUrl}/api/widget/post/${personId}/installments`,
        body,
        { headers: this.getHeaders(), params: this.getParams(token) }
      )
    );
  }

  // ========================================================================
  // POST /api/widget/post/:personId/remanent
  // ========================================================================
  getRemanentInvoice(personId: string, token: string, body: any): Promise<any> {
    return lastValueFrom(
      this.http.post<any>(
        `${this.baseUrl}/api/widget/post/${personId}/remanent`,
        body,
        { headers: this.getHeaders(), params: this.getParams(token) }
      )
    );
  }

  // ========================================================================
  // GET /api/widget/post/:personId/fee-extend
  // ========================================================================
  findExtendFee(personId: string, token: string, code_installment: string, fee: string): Promise<any> {
    return lastValueFrom(
      this.http.get<any>(
        `${this.baseUrl}/api/widget/post/${personId}/fee-extend`,
        { headers: this.getHeaders(), params: this.getParams(token, { code_installment, fee }) }
      )
    );
  }

  // ========================================================================
  // POST /api/widget/post/:personId/verify-bdv
  // ========================================================================
  verifyPaymentBDV(personId: string, token: string, phone: string, amount: string, date: string): Promise<any> {
    return lastValueFrom(
      this.http.post<any>(
        `${this.baseUrl}/api/widget/post/${personId}/verify-bdv`,
        { phone, amount, date },
        { headers: this.getHeaders(), params: this.getParams(token) }
      )
    );
  }

  // ========================================================================
  // POST /api/widget/post/:personId/confirm-bdv
  // ========================================================================
  confirmPaymentBDV(personId: string, token: string, phone: string, amount: string, reference?: string, date?: string): Promise<any> {
    const body: any = { phone, amount };
    if (reference) body.reference = reference;
    if (date) body.date = date;
    return lastValueFrom(
      this.http.post<any>(
        `${this.baseUrl}/api/widget/post/${personId}/confirm-bdv`,
        body,
        { headers: this.getHeaders(), params: this.getParams(token) }
      )
    );
  }

  // ========================================================================
  // POST /api/widget/post/:personId/register-payment
  // El endpoint principal — envía FormData (archivo + params JSON)
  // ========================================================================
  getRegister_payment(personId: string, token: string, formData: FormData): Promise<any> {
    // No enviar content-type header para FormData (browser lo setea con boundary)
    const headers = { 'X-Skip-Auth-Interceptor': 'true' };
    const params = this.getParams(token);
    return lastValueFrom(
      this.http.post<any>(
        `${this.baseUrl}/api/widget/post/${personId}/register-payment`,
        formData,
        { headers, params }
      )
    );
  }
}
